// SiteMap -- North America map with a pin per Active site, colored by
// SHI bucket (same green/amber/red palette as the CHI Dashboard).
// Click a pin for a popup with site + customer info and a link back
// to the customer detail page. Filter row above the map narrows by
// fuel brand, owner, POS type, state/province, and health bucket. A
// colour legend under the filters explains the SHI health buckets.
//
// Presentation mode (open-house / big-screen kiosk): add ?present=1 to
// the URL. It takes over the whole screen with a branded XSite header +
// big live stats and the full map below — normal mode is untouched.
//
// Uses Leaflet + react-leaflet + OpenStreetMap tiles. No API key,
// no cost. Sites need to be geocoded first via server/scripts/
// geocode-sites.js.
//
// Dave 2026-07-09.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Animated count-up for the presentation-mode KPIs: eases a number from
// 0 up to its target on mount, and again whenever the target changes
// (e.g. after a data refresh). Draws the eye on the big open-house
// screen. Honours prefers-reduced-motion and snaps straight to the
// final value for zero, so it never "animates" an empty stat.
function CountUp({ value, duration = 1200 }) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || target === 0) {
      setDisplay(target);
      return;
    }
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic — lively, then settles
      setDisplay(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return <>{display.toLocaleString()}</>;
}

// SHI health -> pin color. Grey = no snapshot yet (new site).
function bucketFor(score) {
  if (score === null || score === undefined) return 'grey';
  if (score >= 80) return 'green';
  if (score >= 50) return 'amber';
  return 'red';
}
const BUCKET_COLORS = {
  green: '#047857',
  amber: '#b45309',
  red:   '#b91c1c',
  grey:  '#6b7280',
};

// Custom SVG pin so we avoid Leaflet's default-icon URL issue and get
// a bucket-colored teardrop for free. cache keyed by color so we build
// each icon once.
const iconCache = {};
function coloredIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.82 20.18 0 13 0z"
            fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
      <circle cx="13" cy="13" r="5" fill="#ffffff"/>
    </svg>`;
  const icon = new L.DivIcon({
    className: 'shi-pin',
    html: svg,
    iconSize:   [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -32],
  });
  iconCache[color] = icon;
  return icon;
}

// Free-text site search. `terms` is a pre-lowercased, pre-split list;
// a site matches when every term appears somewhere in its searchable
// fields (so "esso toronto" narrows to Esso sites in Toronto).
function matchesSearch(s, terms) {
  if (!terms.length) return true;
  const hay = [
    s.site_name, s.site_number, s.company, s.address, s.city,
    s.province_state, s.fuel_brand, s.pos_type, s.owner_name,
  ].filter(Boolean).join(' ').toLowerCase();
  return terms.every((t) => hay.includes(t));
}

// Auto-fit the map to the current marker set whenever the filtered
// list changes. Skips when there are no markers so we don't crash
// Leaflet with a null bounds object.
function FitToMarkers({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (!sites || sites.length === 0) return;
    const bounds = L.latLngBounds(sites.map((s) => [s.latitude, s.longitude]));
    if (!bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [sites, map]);
  return null;
}

// North America default view (roughly). Used before the sites load
// and if no sites match the filters.
const DEFAULT_CENTER = [50, -95];
const DEFAULT_ZOOM   = 4;

export default function SiteMap() {
  const [sites, setSites]           = useState([]);
  const [missing, setMissing]       = useState(0);
  const [totals, setTotals]         = useState({ active_total: 0, geocoded_total: 0 });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [ownerFilter, setOwnerFilter]     = useState('all');
  const [brandFilter, setBrandFilter]     = useState('all');
  const [posFilter, setPosFilter]         = useState('all');
  const [provFilter, setProvFilter]       = useState('all');
  const [bucketFilter, setBucketFilter]   = useState('all');
  const [search, setSearch]               = useState('');

  const [refreshing, setRefreshing]   = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch (or re-fetch) the map data. Pass { silent: true } for a manual
  // refresh: the map stays on-screen with a small spinner instead of
  // blanking to the full loading state. Used both for the initial load
  // and for the Refresh button (handy right after geocoding new sites —
  // the map reads live from the DB, so a refresh pulls them in with no
  // page reload).
  const loadSites = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const r = await axios.get('/api/map/sites');
      setSites(r.data.sites || []);
      setMissing(r.data.missing_geocoding || 0);
      setTotals(r.data.totals || { active_total: 0, geocoded_total: 0 });
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, []);

  useEffect(() => { loadSites(); }, [loadSites]);

  const owners = useMemo(() => {
    const set = new Set(sites.map((s) => s.owner_name).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);
  const brands = useMemo(() => {
    const set = new Set(sites.map((s) => s.fuel_brand).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);
  const posTypes = useMemo(() => {
    const set = new Set(sites.map((s) => s.pos_type).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);
  const provinces = useMemo(() => {
    const set = new Set(sites.map((s) => s.province_state).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);

  // Pre-split the search query once; empty = no search filter.
  const searchTerms = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search]
  );

  const filtered = useMemo(() => {
    return sites.filter((s) => {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) return false;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) return false;
      if (posFilter !== 'all' && s.pos_type !== posFilter) return false;
      if (provFilter !== 'all' && s.province_state !== provFilter) return false;
      if (bucketFilter !== 'all' && bucketFor(s.shi_score) !== bucketFilter) return false;
      if (!matchesSearch(s, searchTerms)) return false;
      return true;
    });
  }, [sites, ownerFilter, brandFilter, posFilter, provFilter, bucketFilter, searchTerms]);

  const bucketCounts = useMemo(() => {
    const c = { green: 0, amber: 0, red: 0, grey: 0 };
    for (const s of sites) {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) continue;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) continue;
      if (posFilter !== 'all' && s.pos_type !== posFilter) continue;
      if (provFilter !== 'all' && s.province_state !== provFilter) continue;
      if (!matchesSearch(s, searchTerms)) continue;
      c[bucketFor(s.shi_score)]++;
    }
    return c;
  }, [sites, ownerFilter, brandFilter, posFilter, provFilter, searchTerms]);

  if (error) return <section className="page"><h2>Site Map</h2><p style={{ color: 'var(--color-bad)' }}>Error: {error}</p></section>;

  // ── Presentation mode (open-house / big-screen kiosk): ?present=1 ──
  const presentMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('present') === '1';
  const provincesServed = Math.max(0, provinces.length - 1); // minus the 'all' entry
  const customersServed = new Set(sites.map((s) => s.customer_id).filter(Boolean)).size;
  const activeTotal = totals.active_total || totals.geocoded_total || sites.length;
  const todayStr = new Date().toLocaleDateString('en-CA', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // Shared map element — identical in normal and presentation layouts.
  const mapEl = (
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      scrollWheelZoom
      style={{ height: '100%', width: '100%' }}
    >
      {/* Basemaps — all keyless (no API key). Default is Esri World
          Street Map so roads and place labels show; topographic, plain
          OpenStreetMap, and a quiet gray canvas are switchable via the
          control in the top-right. */}
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Streets">
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, USGS'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Topographic">
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Esri, HERE, Garmin, USGS, NGA'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="OpenStreetMap">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Light Gray">
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
            maxNativeZoom={16}
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      <FitToMarkers sites={filtered} />
      {filtered.map((s) => {
        const bucket = bucketFor(s.shi_score);
        return (
          <Marker
            key={s.site_id}
            position={[s.latitude, s.longitude]}
            icon={coloredIcon(BUCKET_COLORS[bucket])}
          >
            <Popup>
              <div style={{ minWidth: 220, fontSize: 13 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {s.site_name || '(unnamed site)'}
                </div>
                <div style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                  #{s.site_number || '--'}
                </div>
                <div style={{ marginTop: 6 }}>
                  <Link to={`/customers/${s.customer_id}`}>{s.company}</Link>
                </div>
                <div style={{ marginTop: 6, color: '#374151' }}>
                  {[s.address, s.city, s.province_state].filter(Boolean).join(', ')}
                </div>
                <div style={{
                  marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap',
                  fontSize: 12, color: '#374151',
                }}>
                  {s.fuel_brand && <span><strong>Brand:</strong> {s.fuel_brand}</span>}
                  {s.pos_type && <span><strong>POS:</strong> {s.pos_type}</span>}
                </div>
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px', borderRadius: 4,
                    background: bucket === 'green' ? '#d1fae5' : bucket === 'amber' ? '#fef3c7' : bucket === 'red' ? '#fee2e2' : '#f3f4f6',
                    color:      BUCKET_COLORS[bucket],
                    fontWeight: 700,
                  }}>
                    SHI: {s.shi_score ?? 'n/a'}
                  </span>
                  {s.owner_name && (
                    <span style={{ marginLeft: 8, color: '#6b7280' }}>Owner: {s.owner_name}</span>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );

  if (presentMode) {
    const kpis = [
      { label: 'Sites Served',        value: activeTotal,        color: '#ffffff' },
      { label: 'Provinces & States',  value: provincesServed,    color: '#ffffff' },
      { label: 'Customers',           value: customersServed,    color: '#ffffff' },
      { label: 'Healthy',             value: bucketCounts.green, color: '#34d399' },
      { label: 'Watch',               value: bucketCounts.amber, color: '#fbbf24' },
      { label: 'Needs attention',     value: bucketCounts.red,   color: '#f87171' },
    ];
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 4000, background: '#0b1f33',
        display: 'flex', flexDirection: 'column', padding: 20, boxSizing: 'border-box',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}>
        <style>{`@keyframes xsPulse{0%{opacity:1}50%{opacity:.3}100%{opacity:1}}`}</style>

        {/* Branded hero header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 24, flexWrap: 'wrap',
          background: 'linear-gradient(90deg,#123a63,#1A5FA8)', color: '#fff',
          borderRadius: 14, padding: '18px 30px', marginBottom: 16,
          boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        }}>
          {/* XSite Group logo on a white card so the full-colour mark
              stays crisp against the dark hero. The logo already carries
              the "Gas station and c-store back office" tagline. Asset lives
              at client/public/xsite-logo.png (served at the site root). */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: '14px 22px',
            display: 'flex', alignItems: 'center',
            boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
          }}>
            <img
              src="/xsite-logo.png"
              alt="XSite Group — Gas station and c-store back office"
              style={{ height: 72, width: 'auto', display: 'block' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 34, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {kpis.map((k) => (
              <div key={k.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 46, fontWeight: 800, lineHeight: 1, color: k.color }}>
                  {loading ? '—' : <CountUp value={k.value} />}
                </div>
                <div style={{
                  fontSize: 12.5, opacity: 0.85, marginTop: 6,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
            {/* Refresh + Full-screen (no F11 needed) + Exit back to the normal map. */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => loadSites({ silent: true })}
                disabled={refreshing}
                style={{
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8,
                  padding: '8px 14px', fontSize: 14, fontWeight: 600,
                  cursor: refreshing ? 'default' : 'pointer', fontFamily: 'inherit',
                  opacity: refreshing ? 0.6 : 1,
                }}
                title="Reload the latest site data"
              >
                {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen && document.exitFullscreen();
                  } else {
                    const el = document.documentElement;
                    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
                    req && req.call(el);
                  }
                }}
                style={{
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8,
                  padding: '8px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                title="Toggle full screen"
              >
                ⛶ Full screen
              </button>
              <button
                type="button"
                onClick={() => {
                  if (document.fullscreenElement) { try { document.exitFullscreen(); } catch (_) {} }
                  const u = new URL(window.location.href);
                  u.searchParams.delete('present');
                  window.location.href = u.toString();
                }}
                style={{
                  background: 'transparent', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8,
                  padding: '8px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                title="Return to the normal Site Map"
              >
                ✕ Exit
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, opacity: 0.9 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: '#34d399',
                display: 'inline-block', animation: 'xsPulse 1.6s infinite',
              }} />
              LIVE · {todayStr}{lastUpdated ? ` · updated ${lastUpdated.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </div>
          </div>
        </div>

        {/* Full-bleed map */}
        <div style={{
          flex: 1, minHeight: 0, borderRadius: 12, overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.15)',
        }}>
          {mapEl}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex', gap: 30, alignItems: 'center', justifyContent: 'center',
          marginTop: 12, color: '#cdd7e2', fontSize: 15,
        }}>
          {[
            [BUCKET_COLORS.green, 'Healthy'],
            [BUCKET_COLORS.amber, 'Watch'],
            [BUCKET_COLORS.red,   'Needs attention'],
            [BUCKET_COLORS.grey,  'New site'],
          ].map(([c, l]) => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: c, display: 'inline-block' }} />
              {l}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <section className="page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-height, 56px) - var(--page-pad-y, 24px) * 2)' }}>
      <div className="page-header" style={{ marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Site Map <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>Every active site, colored by health</span></h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${totals.geocoded_total} geocoded sites shown`}
            {missing > 0 && !loading && (
              <span style={{ marginLeft: 8, color: 'var(--color-warn)' }}>
                ({missing} active site{missing === 1 ? '' : 's'} missing coordinates -- run geocode-sites.js)
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {/* Reload the latest site data without a full page reload — handy
              right after geocoding newly added sites. */}
          <button
            type="button"
            onClick={() => loadSites({ silent: true })}
            disabled={refreshing || loading}
            style={{
              background: 'var(--color-surface, #ffffff)',
              color: 'var(--color-text, #123a63)',
              border: '1px solid var(--color-border, #cbd5e1)',
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
              cursor: (refreshing || loading) ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: (refreshing || loading) ? 0.6 : 1,
            }}
            title="Reload the latest site data (use after geocoding new sites)"
          >
            {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
          </button>
          {/* Enter the big-screen branded view (adds ?present=1 to the URL,
              which is also bookmarkable). Exit is a button inside that view. */}
          <button
            type="button"
            onClick={() => {
              const u = new URL(window.location.href);
              u.searchParams.set('present', '1');
              window.location.href = u.toString();
            }}
            style={{
              background: '#123a63', color: '#fff', border: 0,
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
            title="Full-screen branded view for the big screen / open house"
          >
            ▶ Presentation mode
          </button>
        </div>
      </div>

      {/* Filter row + clickable bucket chips */}
      <div style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
        flexWrap: 'wrap', marginBottom: 'var(--space-3)',
      }}>
        {/* Free-text site search — narrows the pins and zooms the map to
            matches (site name, #, company, address, city, brand, owner). */}
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search site, #, city, brand…"
            aria-label="Search sites"
            style={{
              padding: '6px 26px 6px 10px', minWidth: 240, fontFamily: 'inherit',
              fontSize: 'inherit', border: '1px solid var(--color-border, #cbd5e1)',
              borderRadius: 6,
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              title="Clear search"
              style={{
                position: 'absolute', right: 4, border: 0, background: 'transparent',
                cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#6b7280', padding: 2,
              }}
            >×</button>
          )}
        </div>

        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
          {owners.map((o) => <option key={o} value={o}>{o === 'all' ? 'All owners' : o}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
          {brands.map((b) => <option key={b} value={b}>{b === 'all' ? 'All brands' : b}</option>)}
        </select>
        <select value={posFilter} onChange={(e) => setPosFilter(e.target.value)}>
          {posTypes.map((p) => <option key={p} value={p}>{p === 'all' ? 'All POS types' : p}</option>)}
        </select>
        <select value={provFilter} onChange={(e) => setProvFilter(e.target.value)}>
          {provinces.map((p) => <option key={p} value={p}>{p === 'all' ? 'All states/provinces' : p}</option>)}
        </select>

        {/* Bucket chips -- same click-to-filter interaction as CHI Dashboard */}
        {[
          { key: 'green', label: 'Green', bg: '#d1fae5', fg: '#047857', count: bucketCounts.green },
          { key: 'amber', label: 'Amber', bg: '#fef3c7', fg: '#b45309', count: bucketCounts.amber },
          { key: 'red',   label: 'Red',   bg: '#fee2e2', fg: '#b91c1c', count: bucketCounts.red   },
        ].map((chip) => {
          const isActive = bucketFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setBucketFilter(isActive ? 'all' : chip.key)}
              style={{
                background: chip.bg, color: chip.fg,
                padding: '4px 10px', borderRadius: 4, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit',
                border: `2px solid ${isActive ? BUCKET_COLORS[chip.key] : 'transparent'}`,
              }}
            >
              {chip.label}: {chip.count}{isActive && ' x'}
            </button>
          );
        })}
        {bucketFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setBucketFilter('all')}
            style={{
              padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)', color: 'var(--color-text-dim)',
              fontFamily: 'inherit', fontSize: 'inherit',
            }}
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Health colour legend -- what each pin colour means, with the
          SHI score range behind each bucket. Mirrors bucketFor(). */}
      <div style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
        flexWrap: 'wrap', marginBottom: 'var(--space-3)',
        fontSize: 12, color: 'var(--color-text-dim)',
      }}>
        <span style={{ fontWeight: 600 }}>Pin colour = site health (SHI):</span>
        {[
          { c: BUCKET_COLORS.green, label: 'Healthy',         range: '80–100' },
          { c: BUCKET_COLORS.amber, label: 'Watch',           range: '50–79' },
          { c: BUCKET_COLORS.red,   label: 'Needs attention', range: 'below 50' },
          { c: BUCKET_COLORS.grey,  label: 'No score yet',    range: 'new site' },
        ].map((item) => (
          <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 11, height: 11, borderRadius: '50%', background: item.c,
              display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0,
            }} />
            {item.label} <span style={{ opacity: 0.75 }}>({item.range})</span>
          </span>
        ))}
      </div>

      {/* Map fills the remaining vertical space */}
      <div style={{
        flex: 1, minHeight: 400, border: '1px solid var(--color-border)',
        borderRadius: 6, overflow: 'hidden',
      }}>
        {mapEl}
      </div>
    </section>
  );
}
